import React, { JSXElementConstructor, ReactElement } from 'react';
import { IActionContextAPI } from '@wix/editor-platform-host-integration-apis';
import { PlatformContext } from '../types';
import { BaseActionPanelAPI } from './BaseActionPanelAPI';
import { IActionContextContributor } from './IActionContextContributor';
import {
  ElementsPanelProps,
  ElementsPanelPropsWithBi,
} from '@wix/editor-platform-feature-store';

import { DisplayElementsPanelService } from '../../displayElementsPanelService';

type UpdatePropsCallback = (
  data: Pick<ElementsPanelProps, 'data' | 'items' | 'onToggleState'>,
) => void;

let activeUnsubscribe: (() => void) | undefined;

export class DisplayElementsActionPanelAPI
  extends BaseActionPanelAPI
  implements IActionContextContributor
{
  #updatePropsCallback: UpdatePropsCallback | null = null;
  #displayPanelService: DisplayElementsPanelService;

  #editorPointer;
  #panelAPI;

  constructor(readonly context: PlatformContext) {
    super();

    this.#displayPanelService = new DisplayElementsPanelService({
      ...context,
      editorPointer: this.context.editorPointers[0],
      platformApiMethods: this.context.hostAPI,
    });

    this.#panelAPI =
      this.context.editorPlatformPackagesBridge?.PlatformElementsPanelAPI;
    this.#editorPointer = this.context.editorPointers[0];
  }

  #subscribeOnStateChange() {
    const unsubscribeStates = this.context.componentsAPI.components.onChange(
      [this.#editorPointer],
      ({ type }) => {
        if (type === 'state') {
          const newPanelProps = this.#getProps();

          if (newPanelProps) {
            this.#updatePropsCallback?.(newPanelProps);
          }
        }
      },
    );

    activeUnsubscribe = unsubscribeStates;
  }

  #getProps(): ElementsPanelPropsWithBi | null {
    const widgetName = this.context.componentsAPI.components.getDisplayName(
      this.#editorPointer,
    );

    const panelProps = this.#displayPanelService.getProps();

    return {
      ...panelProps,
      biParams: {
        component_type: this.context.componentsAPI.components.getComponentType(
          this.#editorPointer,
        ),
        component_id: this.#editorPointer.documentPointer.id,
      },
      widgetName,
      onUpdateProps: (callback: UpdatePropsCallback) => {
        this.#updatePropsCallback = callback;
      },
    };
  }

  getActionContextOverrides(): Partial<IActionContextAPI> {
    return {
      getActionableElements: () =>
        this.#displayPanelService.getActionableElements(),
    };
  }

  getContent(): React.ReactElement | undefined {
    this.#displayPanelService.setRespectIndeterminate(true);
    const panelProps = this.#getProps();

    if (!this.#panelAPI || !panelProps) {
      return;
    }

    activeUnsubscribe?.();
    this.#subscribeOnStateChange();

    return this.#panelAPI.getPanelContent(panelProps) as React.ReactElement;
  }

  getMinimizedContent():
    | ReactElement<any, string | JSXElementConstructor<any>>
    | undefined {
    this.#displayPanelService.setRespectIndeterminate(false);
    const panelProps = this.#getProps();

    if (!this.#panelAPI || !panelProps) {
      return;
    }

    activeUnsubscribe?.();
    this.#subscribeOnStateChange();

    return this.#panelAPI.getMinimizedContent(panelProps) as React.ReactElement;
  }

  async getHostContext() {
    return this.getInfraSDKHostContext({});
  }
}
